import {
  EActionCategory,
  ECardinality,
  ESdChangeReason,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  ISymbol,
  isComplexObjectParameter
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import isEqual from "react-fast-compare";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { getActionParameterDoc } from "../../../../shared/utility/action-doc";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDrawer from "../../../ui-kit/kuika-drawer/kuika-drawer";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { EActionPanelStatus } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import "./action-section.scss";
import { SymbolPicker } from "./symbol-picker";

/** antd's default drawer width, which every level of an object parameter opened at. */
const OBJECT_PARAMETER_DRAWER_WIDTH: number = 256;

export interface IActionSymbolWithChildState {
  isCollapsed: boolean;
}

export interface IActionSymbolWithChildProps {
  parameter: IActionParameter;
  action: IAction;
  actionDefinition: IActionDefinitionDto;
  actionPanelStatus: EActionPanelStatus;
  index?: number;
  getSelectboxParameter?: (parameter: IActionParameter) => any;
  onActionParameterSelected?: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  onActionSaved?: (action: IAction, changeReason?: ESdChangeReason) => void;
  onDeleteParameter?: (parameterName: string) => void;
}

class ActionSymbolWithChild extends PureSdComponent<
  IActionSymbolWithChildProps & IWithSDProps & IWithContextProp,
  IActionSymbolWithChildState
> {
  constructor(props: IActionSymbolWithChildProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isCollapsed: true
    };
  }

  onSymbolPickedOnAction = (symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const stateParameter = this.props.action.parameters.find((p) => p.name == this.props.parameter.name);
    const updatedParameter = { ...this.props.parameter, symbol };

    if (stateParameter) stateParameter.symbol = symbol;

    this.props.onActionParameterSelected &&
      this.props.onActionParameterSelected(_.cloneDeep(updatedParameter), _.cloneDeep(symbol), changeReason);

    if (
      isComplexObjectParameter(updatedParameter) &&
      this.props.parameter.symbol.targetType?.split(".").pop() !== this.props.parameter.Typename
    ) {
      this.toggleObjectParameter();
    }
  };

  drawerClosed = () => {
    this.toggleObjectParameter();
  };

  getSelectBoxParameters: any = (parameter: IActionParameter) => {
    if (this.props.getSelectboxParameter === undefined) {
      return undefined;
    }
    return this.props.getSelectboxParameter(parameter);
  };

  // Collapsed means the object's drawer is shut, so the control offers to open it. The label read
  // the other way round, calling the arrow that opens the drawer "Collapse Object".
  getObjectToggleLabel = (): string => (this.state.isCollapsed ? "Expand Object" : "Collapse Object");

  renderSymbolTitle = (p: IActionParameter, parameterOptions: any) => {
    // Unrelated to the label's own formatting (that's getActionParameterLabel's job): this gate
    // decides whether the type/cardinality chips show AT ALL for this parameter and must stay here.
    const showTypeChips: boolean = parameterOptions === undefined || p.isEnum === true;
    return (
      <>
        <span className="kuika-action-section__param">
          <ActionParameterTitle
            parameter={p}
            showTypeChips={showTypeChips}
            doc={getActionParameterDoc(this.props.actionDefinition, p.name)}
          />
          <span
            style={{
              display: isComplexObjectParameter(p) ? "block" : "none",
              margin: "6px",
              fontSize: "15px"
            }}
          >
            <KuikaTooltip content={this.getObjectToggleLabel()}>
              <KuikaButton
                isIcon
                type="tertiary"
                color="neutral"
                aria-label={this.getObjectToggleLabel()}
                onClick={(): void => this.toggleObjectParameter()}
              >
                <KuikaSvg
                  type="common"
                  name={this.state.isCollapsed ? "square-chevron-right" : "square-chevron-down"}
                  width={16}
                  height={16}
                />
              </KuikaButton>
            </KuikaTooltip>
          </span>
        </span>
      </>
    );
  };

  renderChildPickers = (parameter: IActionParameter, parameterOptions: any, index: number) => {
    // KOA-18737: a raw-JSON (JObject / JArray / JToken) parameter has no nested fields — no drawer, no
    // "recursive parameter" dead end; it is bound as one value like a scalar.
    if (isComplexObjectParameter(parameter) && !this.state.isCollapsed) {
      const calculatedIndex: number = index + 1;
      // if (parameter.parameters === null || parameter.parameters === undefined || parameter.parameters.length === 0) {
      // const action = _.cloneDeep(this.props.action);
      // const parentParameterName = parameter.name.substring(0, parameter.name.lastIndexOf('_'));
      // var parentParameter = KMainFunctions.findParameter(_.cloneDeep(action.parameters), parentParameterName);
      // if (parameter.Typename == parentParameter?.Typename) {
      //   parameter.parameters = parentParameter?.parameters
      // }
      // }
      return (
        <>
          <KuikaDrawer
            header={
              <>
                <h5>{parameter.displayName ?? parameter.name}</h5>
                <span style={{ fontSize: "11px", lineHeight: "18px" }}>
                  <div>{parameter.symbol?.description}</div>
                  <div>
                    {parameter.symbol?.targetType?.toString().split(".").pop()} - ({ECardinality[parameter.cardinality]}
                    )
                  </div>
                </span>
              </>
            }
            isOpen={!this.state.isCollapsed}
            onClose={(): void => this.drawerClosed()}
            width={OBJECT_PARAMETER_DRAWER_WIDTH}
            // The symbol picker inside is built on antd's Cascader, whose popup mounts on the body; a
            // modal drawer would leave it painted but unable to take a click.
            modal={false}
            maskClosable
            push={OBJECT_PARAMETER_DRAWER_WIDTH}
            className="kuika-drawer-object-parameter"
            bodyClassName="kuika-drawer-object-parameter__body"
          >
            <div>
              {parameter.parameters?.map((p) => {
                return (
                  <ActionSymbolWithChild
                    key={`actionSymbolWithChild_${this.props.action.id}_${parameter.name}_${p.name}`}
                    parameter={_.cloneDeep(p)}
                    action={_.cloneDeep(this.props.action)}
                    actionDefinition={this.props.actionDefinition}
                    actionPanelStatus={EActionPanelStatus.new}
                    getSelectboxParameter={(p) => this.getSelectBoxParameters(p)}
                    index={calculatedIndex}
                    onActionParameterSelected={(
                      parameter: IActionParameter,
                      symbol: ISymbol,
                      changeReason?: ESdChangeReason
                    ) =>
                      this.props.onActionParameterSelected &&
                      this.props.onActionParameterSelected(parameter, symbol, changeReason)
                    }
                    onActionSaved={(action: IAction, changeReason?: ESdChangeReason) =>
                      this.props.onActionSaved && this.props.onActionSaved(action, changeReason)
                    }
                    onDeleteParameter={(parameterName: string) => {
                      this.props.onDeleteParameter && this.props.onDeleteParameter(parameterName);
                    }}
                    sd={this.props.sd}
                    context={this.props.context}
                  ></ActionSymbolWithChild>
                );
              })}
              {(parameter.parameters === null ||
                parameter.parameters === undefined ||
                parameter.parameters.length === 0) && (
                <div>
                  This is a recursive parameter and can not be mapped. Use parameter itself to map to an action result.
                </div>
              )}
            </div>
          </KuikaDrawer>
        </>
      );
    }
  };

  toggleObjectParameter = () => {
    this.setState((prevState) => ({ isCollapsed: !prevState.isCollapsed }));
  };

  // Hover help shown on a parameter's Fixed Value field, matching the component-property
  // tooltip idiom (property.description) used in property-form-item.
  getParameterHelpText = (parameter: IActionParameter): string | undefined => {
    if (this.props.actionDefinition?.name !== "GetAuditLogs") return undefined;
    if (parameter.name === "targetType") {
      return "Optional filter by target type. Allowed values: Sql, Action, Http. Leave empty for no filter.";
    }
    if (parameter.name === "pageSize") {
      return "Number of records per page. Default 50, maximum 200 (higher values are capped to 200).";
    }
    return undefined;
  };

  renderSymbolPicker = (parameter, selectboxParameter) => {
    if (
      parameter.name === "SortBy" &&
      (this.props.actionDefinition?.actionCategory === EActionCategory.ProcessAdministration ||
        this.props.actionDefinition?.actionCategory === EActionCategory.ProcessAutomation)
    ) {
      const actionReturnType: any =
        this.props.actionDefinition?.returnTypeModel ??
        (this.props.action as any)?.returnType ??
        parameter.symbol?.targetTypeDefinition;

      const properties: any[] =
        actionReturnType?.elements ??
        actionReturnType?.members ??
        (actionReturnType && typeof actionReturnType === "object"
          ? Object.keys(actionReturnType).map((k) => ({
              name: k,
              type: (actionReturnType as any)[k]
            }))
          : []);

      const options =
        properties && properties.length
          ? properties.map((p: any) => ({
              value: p.name ?? p.key ?? p,
              label: p.displayName ?? p.name ?? p.key ?? String(p)
            }))
          : [{ value: "", label: "No return properties available" }];

      return (
        <KuikaSelectbox
          value={parameter.symbol?.fixedValue}
          options={options}
          onChange={(value) => {
            const symbol = _.cloneDeep(parameter.symbol);
            symbol.fixedValue = value;
            this.onSymbolPickedOnAction(symbol);
          }}
        />
      );
    }
    const picker: JSX.Element = (
      <SymbolPicker
        changeType="onBlur"
        valueType={parameter.Typename}
        targetCardinality={parameter.cardinality}
        onSelectedSymbol={this.onSymbolPickedOnAction}
        action={_.cloneDeep(this.props.action)}
        parameter={_.cloneDeep(parameter)}
        symbol={_.cloneDeep(parameter.symbol)}
        parameterOptions={selectboxParameter?.options}
        parameterSelectboxDropdownHeader={selectboxParameter?.dropdownHeader}
        parameterSelectboxDropdownHeaderClick={selectboxParameter?.onDropdownHeaderClick}
        parameterSelectboxOptionRenderer={selectboxParameter?.optionRenderer}
        parameterSelectboxIsLoading={selectboxParameter?.isLoading}
        isMultiSelect={selectboxParameter?.isMultiSelect}
        hideActionResults={selectboxParameter?.hideActionResults}
        hideCategoryChange={selectboxParameter?.hideCategoryChange}
        hideFixedValue={selectboxParameter?.hideFixedValue}
        allowConditionValue={selectboxParameter === undefined}
        hideWFParams={selectboxParameter?.hideWFParams}
        isObject={isComplexObjectParameter(parameter)}
        parameterSelectClassName={selectboxParameter?.selectClassName}
      />
    );

    const helpText: string | undefined = this.getParameterHelpText(parameter);
    if (helpText) {
      return (
        <KuikaTooltip content={helpText}>
          <div style={{ display: "block" }}>{picker}</div>
        </KuikaTooltip>
      );
    }

    return picker;
  };

  render() {
    const parameter: IActionParameter = this.props.parameter;
    const selectboxParameter = this.getSelectBoxParameters(parameter);
    return (
      <div className={parameter.isDeletedOnSource === true ? "isDeletedOnSource" : ""}>
        {this.renderSymbolTitle(parameter, selectboxParameter)}
        {this.renderSymbolPicker(parameter, selectboxParameter)}
        {this.renderChildPickers(parameter, selectboxParameter, this.props.index ?? 0)}
        {parameter.isDeletedOnSource === true && (
          <div className="kuika_styling_typography_footer" style={{ padding: "4px 6px", display: "flow-root" }}>
            {this.props.actionPanelStatus === EActionPanelStatus.edit && (
              <div
                style={{ float: "right" }}
                onClick={() => this.props.onDeleteParameter && this.props.onDeleteParameter(parameter.name)}
              >
                Delete
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  componentDidUpdate(prevProps: IActionSymbolWithChildProps) {
    if (prevProps.parameter.name !== this.props.parameter.name) {
      this.setState({ isCollapsed: true });
    }
  }

  checkNeedRender = (
    changeReason: ESdChangeReason,
    nextProps: IActionSymbolWithChildProps & IWithSDProps & IWithContextProp
  ): boolean => {
    if (!isEqual(this.props.parameter, nextProps.parameter) || !isEqual(this.props.action, nextProps.action)) {
      return true;
    }
    return false;
  };
}

const actionSymbolWithChild = withContext<IActionSymbolWithChildProps>(
  withSD<IActionSymbolWithChildProps>(ActionSymbolWithChild)
);
export { actionSymbolWithChild as ActionSymbolWithChild };
